// eslint-disable-next-line import/no-cycle
import { isHtmlElement, isDocumentFragment } from '@slickdeals/durango/dist/javascript/utilities/guards';
// eslint-disable-next-line import/no-cycle
import { isJsxComponent } from '../utilities/guards';
import { parseProps } from '../utilities/jsxPropParser';
// eslint-disable-next-line import/no-cycle
import BlueprintComponent from './blueprintComponent';
/**
 * Tests to see if passed element is a node tag
 *
 * @param {string | any} nodeTag Element to test
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isNodeTag = (nodeTag) => typeof nodeTag === 'string';
class JsxComponent extends BlueprintComponent {
    /**
     * Parses Jsx code
     *
     * @param {JsxTagIdentifiers} nodeTagOrModule A node tag or JsxComponent module
     * @param {JsxComponentProps} props Properties of nodeTagOrModule
     * @param {Children[]} children Children of nodeTagOrModule
     */
    // eslint-disable-next-line consistent-return
    static parseJsx(nodeTagOrModule, props, ...children) {
        if (isNodeTag(nodeTagOrModule)) {
            return JsxComponent.createHtmlElement(nodeTagOrModule, props, ...children);
        }
        // Instantiate module with options
        const instance = new nodeTagOrModule(props?.options); // eslint-disable-line new-cap
        // Type guard - we already know that instance is a JsxComponent but without explicitly checking,
        // TypeScript can't infer that a createElement call exists on instance
        if (isJsxComponent(instance)) {
            const element = instance.createElement(props);
            if (children) {
                return JsxComponent.handleChildren(element, ...children);
            }
        }
    }
    /**
     * Controller function for handling creation of HTMLElement
     *
     * @param {string} nodeTag Node tag of an HTMLElement
     * @param {JsxComponentProps} props Properties to add onto creates HTMLElement
     * @param {Children[]} children Children of HTMLElement
     */
    static createHtmlElement(nodeTag, props, ...children) {
        let element;
        if (nodeTag === 'fragment') {
            element = document.createDocumentFragment();
        }
        else {
            element = document.createElement(nodeTag);
        }
        if (children) {
            element = JsxComponent.handleChildren(element, ...children);
        }
        if (props) {
            element = parseProps(element, props);
        }
        return element;
    }
    /**
     * Handles addition of children onto passed element
     *
     * @param {HTMLElement} element Element to append children to
     * @param {Children[]} children Children to append onto element
     */
    static handleChildren(element, ...children) {
        children.forEach(child => {
            /**
             * If child is an array, only allow for one array deep of processing.
             * Otherwise, recursion can go too deep and error out. Plus, if you have
             * arrays that are more than one level deep, then your logic needs to be revaluated
             */
            if (Array.isArray(child)) {
                child.forEach((childElement) => JsxComponent.handleChild(element, childElement));
                return;
            }
            JsxComponent.handleChild(element, child);
        });
        return element;
    }
    /**
     * Handles an individual child
     * @param {HTMLElement} element Element to append child to
     * @param {Children} child Child to append onto element
     */
    static handleChild(element, child) {
        if (isHtmlElement(child) || isDocumentFragment(child)) {
            element.appendChild(child);
            return;
        }
        if (typeof child === 'string') {
            const textNode = document.createTextNode(child);
            element.appendChild(textNode);
            return;
        }
        if (typeof child === 'number') {
            element.appendChild(document.createTextNode(child.toString(10)));
        }
    }
}
export default JsxComponent;
